/**
 * Whose entries the Log shows — MINE by default (Theresa, Sep 2026).
 *
 * The page opened on everyone's activity. That is the right capability (covering
 * a colleague on holiday is exactly what it is for) and the wrong default: a
 * recruiter arriving to check their own work had to filter themselves out of
 * eight other people's first, on every one of the page's sections.
 *
 * So "mine" is the default and "everyone" is one click away. Nothing is hidden
 * that was not hidden before — the page is read-only and this changes only what
 * it opens on.
 *
 * "Mine" means different fields in different sections, which is why the
 * predicates live here rather than being re-derived per panel:
 *
 *   Interviews  — I own the role OR I am on the panel. Both, deliberately: a
 *                 recruiter owns far more interviews than they sit in, and the
 *                 page's existing Interviewer dropdown matches only the second.
 *                 Defaulting to that alone would have hidden most of their work.
 *   Decisions   — I recorded the decision (`decidedBy`).
 *   Sent emails — the board sent it on my behalf (`sentBy`).
 *
 * The Screening log has no per-person field at all: it is the rejection
 * automation's own activity, counted by role. It is left whole and says so.
 */
import type { InterviewRow } from '../services/interviewsApi';

export type LogScope = 'mine' | 'everyone';

/** Addresses arrive from Lever, Firestore and Firebase; only case and padding differ. */
const same = (a?: string | null, b?: string | null) =>
  !!a && !!b && a.trim().toLowerCase() === b.trim().toLowerCase();

/** Mine when I own the role or I am on the panel — see the note above. */
export const isMyInterview = (r: InterviewRow, me?: string | null) =>
  same(r.taOwner, me) || r.interviewers.some((e) => same(e, me));

/** Shared by decisions (`decidedBy`) and sent emails (`sentBy`). */
export const isMine = (who: string | null | undefined, me?: string | null) => same(who, me);

/**
 * The state counts the Interviews dropdown shows.
 *
 * Scoping to one person makes the server's summary wrong — it counts the whole
 * team — and a dropdown reading "All (16)" above one visible row is the kind of
 * mismatch that gets reported as data loss. Computed from the rows on screen
 * instead, so the number and the list cannot disagree.
 */
export const countStates = (rows: InterviewRow[]) => ({
  total: rows.length,
  upcoming: rows.filter((r) => r.upcoming).length,
  rescheduled: rows.filter((r) => r.rescheduleCount > 0).length,
  cancelled: rows.filter((r) => r.status === 'cancelled').length,
});

/** Two buttons, not a dropdown: there are exactly two answers and one is the default. */
export function ScopeToggle({
  scope,
  onChange,
}: {
  scope: LogScope;
  onChange: (next: LogScope) => void;
}) {
  return (
    <div className="mb-4 flex items-center gap-2">
      <span className="text-xs font-medium text-gray-500">Showing</span>
      <div className="flex gap-1.5" role="group" aria-label="Whose entries to show">
        {([
          ['mine', 'Mine'],
          ['everyone', 'Everyone'],
        ] as const).map(([id, label]) => (
          <button
            key={id}
            type="button"
            aria-pressed={scope === id}
            onClick={() => onChange(id)}
            className={
              scope === id
                ? 'rounded-full border border-[#1A1A1A] bg-[#1A1A1A] px-3 py-1 text-sm font-medium text-white'
                : 'rounded-full border border-gray-300 bg-white px-3 py-1 text-sm font-medium text-gray-700 hover:border-gray-500'
            }
          >
            {label}
          </button>
        ))}
      </div>
      {scope === 'everyone' && (
        <span className="text-xs text-gray-400">Everyone's entries — for covering a colleague.</span>
      )}
    </div>
  );
}
